import { BybAvatar } from "@Components/common/byb-ds";
import { surfaceV100Background } from "@Components/common/byb-ds/buttons/styled-components";
import EditPendingInviteDialog from "@Components/game/settings/dialogs/edit-pending-invite-dialog";
import CenteredLoadingOverlay from "@Components/generic/centered-loading-overlay";
import ContainerWrapper from "@Components/generic/container-wrapper";
import Icon from "@Components/generic/icon";
import Section from "@Containers/shared/cards/section";
import TitleWrapper from "@Containers/shared/title-wrapper";
import { MembersProvider, useMembers } from "@Context/common/members/members-context";
import Ownership from "@Context/common/permissions/ownership";
import Permissions from "@Context/common/permissions/permissions";
import AccountRoles from "@Enums/AccountRoles";
import { faChevronRight, faEnvelope, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { formatName } from "@Helpers/formatters";
import { MemberOrInvite } from "@hooks/common/members/use-members";
import useI18n from "@i18n";
import { Box, Button, Chip, Container, Stack, Typography, useTheme } from "@mui/material";

const MembersContent = () => {
  const { i18n } = useI18n();
  const theme = useTheme();
  
  const {
    filteredMembers,
    isLoading,
    userId,
    globalState,
    activeFilter,
    selectedPendingInvite,
    setActiveFilter,
    setSelectedPendingInvite,
    handleNavigateToMember,
    handleInviteMember,
    handleDeleteInvite,
    handleResendInvite,
  } = useMembers();

  const renderMemberItem = (member: MemberOrInvite) => {
    const isCurrentUser = !member.isPending && member.id === userId;

    return (
      <Box key={member.isPending ? `invite-${member.invite.id}` : `member-${member.id}`} sx={{ position: "relative" }}>
        <Button
          color="secondary"
          fullWidth
          onClick={() => {
            if (member.isPending) {
              setSelectedPendingInvite(member);
            } else {
              handleNavigateToMember(member.id);
            }
          }}
          sx={theme => ({
            padding: theme.spacing(2.5),
            borderRadius: theme.spacing(2),
          })}
          disableRipple
          disableFocusRipple
          disableTouchRipple
        >
          <Stack direction="row" spacing={2.5} justifyContent="space-between" sx={{ width: "100%", alignItems: "center" }}>
            <BybAvatar src={member.isPending ? "" : member.avatar || ""} size={8} user={!member.isPending ? member : undefined} />
            <Stack spacing={0.5} sx={{ flexGrow: 1, textAlign: "left" }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="text1xl600" color="dsBase.v200">
                  {member.isPending ? member.email : formatName(member.first_name, member.last_name)}
                </Typography>
                {isCurrentUser && (
                  <Typography variant="text1xl600" color="dsNeutral.v300">
                    {`(${i18n("settings.members.you")})`}
                  </Typography>
                )}
                {member.isPending && (
                  <Chip
                    label={i18n("settings.members.pending")}
                    size="small"
                    sx={{
                      position: "absolute",
                      right: theme.spacing(2),
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: "warning.main",
                      color: "warning.contrastText",
                      height: "20px",
                      ml: 1,
                    }}
                  />
                )}
              </Stack>
              <Typography variant="textLg500" color="dsNeutral.v300">
                {i18n(`roles.${member.role.name}`)}
              </Typography>
            </Stack>
          </Stack>
        </Button>
        {!member.isPending && (
          <Icon
            icon={faChevronRight}
            size="xs"
            color={theme.palette.dsNeutral.v300}
            sx={{
              position: "absolute",
              right: theme.spacing(4),
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <ContainerWrapper id="SETTINGS-MEMBERS" sx={{ overflow: "visible" }}>
      <CenteredLoadingOverlay loading={isLoading} />
      <Container maxWidth="sm" sx={theme => ({ pb: theme.spacing(14) })}>
        <TitleWrapper title={i18n("settings.members.title")} sx={{ position: "relative" }}>
          <Stack direction="row" spacing={1.5} sx={{ position: "absolute", top: 0, right: 0 }}>
            <Permissions editor={AccountRoles.ADMIN} viewer={AccountRoles.ADMIN}>
              <Button
                onClick={() => setActiveFilter(activeFilter === "invites" ? "all" : "invites")}
                sx={theme => ({
                  textTransform: "none",
                  backgroundColor: activeFilter === "invites" ? theme.palette.dsPrimary.v200 : theme.palette.dsSurface.v200,
                  color: activeFilter === "invites" ? theme.palette.dsPrimary.v200 : theme.palette.dsBase.v200,
                  borderRadius: theme.spacing(3),
                  padding: theme.spacing(0.5, 1),
                  height: "40px",
                  minWidth: "116px",
                  "&:hover": {
                    backgroundColor: activeFilter === "invites" ? theme.palette.dsPrimary.v300 : theme.palette.dsSurface.v300,
                  },
                })}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={theme => ({
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: activeFilter === "invites" ? "rgba(255, 255, 255, 0.2)" : theme.palette.dsSurface.v300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    })}
                  >
                    <Icon
                      icon={faEnvelope}
                      size="sm"
                      color={activeFilter === "invites" ? theme.palette.dsPrimary.v300 : theme.palette.dsNeutral.v300}
                    />
                  </Box>
                  <Typography variant="textLg500">{i18n("settings.members.filter.invites")}</Typography>
                </Stack>
              </Button>
            </Permissions>
          </Stack>
          <Stack spacing={2}>
            <Section
              title={i18n("settings.members.section.profiles", {
                count: filteredMembers.length,
                available: globalState.account?.available_seats || 0,
              })}
            >
              <Stack spacing={1.5}>
                <Permissions editor={AccountRoles.ADMIN} viewer={AccountRoles.ADMIN}>
                  <Button
                    color="secondary"
                    onClick={handleInviteMember}
                    disabled={!globalState.account?.available_seats}
                    sx={theme => ({
                      padding: theme.spacing(2.5),
                      borderRadius: theme.spacing(2),
                      "&:hover": {
                        opacity: globalState.account?.available_seats ? 0.9 : 1,
                      },
                      "&.Mui-disabled": {
                        opacity: 0.5,
                        cursor: "not-allowed",
                      },
                    })}
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                  >
                    <Stack direction="row" spacing={2.5} justifyContent="space-between" sx={{ width: "100%", alignItems: "center" }}>
                      <Box
                        sx={theme => ({
                          width: theme.spacing(8),
                          height: theme.spacing(8),
                          borderRadius: "50%",
                          backgroundColor: "dsSurface.v200",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        })}
                      >
                        <Icon icon={faPlusCircle} fontSize={24} color={theme.palette.dsNeutral.v300} />
                      </Box>
                      <Stack spacing={0.5} sx={{ flexGrow: 1, textAlign: "left" }}>
                        <Typography variant="text1xl600" color="dsBase.v200">
                          {i18n("settings.members.invite-member")}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="textLg500"
                        color="dsNeutral.v300"
                        sx={{
                          ml: 2,
                          minWidth: "fit-content",
                        }}
                      >
                        {i18n("settings.members.profiles-remaining", { count: globalState.account?.available_seats || 0 })}
                      </Typography>
                    </Stack>
                  </Button>
                </Permissions>
                <Ownership userId={userId!}>
                  <Permissions ownerCanEdit={true} editor={AccountRoles.ADMIN} viewer={AccountRoles.GUEST}>
                    {filteredMembers.map(renderMemberItem)}
                  </Permissions>
                </Ownership>
              </Stack>
            </Section>
          </Stack>
        </TitleWrapper>
      </Container>

      {selectedPendingInvite && (
        <EditPendingInviteDialog
          open={Boolean(selectedPendingInvite)}
          invite={selectedPendingInvite.invite}
          onDelete={() => handleDeleteInvite(selectedPendingInvite.invite.id)}
          onResend={() => handleResendInvite(selectedPendingInvite)}
          onCancel={() => setSelectedPendingInvite(null)}
        />
      )}
    </ContainerWrapper>
  );
};

const Members = () => (
  <MembersProvider>
    <MembersContent />
  </MembersProvider>
);

export default Members;
